/**
 * @file OrderSummary.tsx
 * @description Full-height "Your order summary" panel for the Learning Materials cart.
 * Rendered in a portal with backdrop, Escape-to-close, focus restore and body scroll
 * lock. Lets the visitor edit per-item quantities and remove items from the bag.
 */

'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { orderSummaryVariants } from './LearningMaterials.styles';
import { useCart, injectNumberOfItemsWithinLabel } from './CartProvider';
import { IForms, ILearningMaterials } from '@/.generated';
import RichTextWrapper from '@/helpers/Wrappers/RichTextWrapper/RichTextWrapper';
import { Forms } from '../Forms/Forms';
import { trackAdobeClick } from '@/utils/adobe/click';

type OrderSummaryProps = ILearningMaterials['order_summary'] & {
  forms?: IForms;
  // Whether the panel is currently the active view; kept mounted (see CartBar) so
  // in-progress form input survives a close/reopen instead of being reset by unmount.
  isOpen: boolean;
};

export const OrderSummary = (props: OrderSummaryProps) => {
  const { isOpen } = props;
  const {
    items,
    totalItems,
    updateQuantity,
    removeItem,
    closeCart,
    maxAllowedQuantity,
    maxQuantityErrorMessage,
    previousFocusRef,
  } = useCart();
  const panelRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef(items);
  useEffect(() => { itemsRef.current = items; }, [items]);

  // Maps cart items to the form's `items` datagrid format on submission
  const getSubmissionData = useCallback(() => ({
    items: itemsRef.current.map((cartItem) => ({
      quantity: cartItem.quantity,
      item: cartItem.title?.replace(/<[^>]*>/g, '') ?? '',
    })),
  }), []);

  // Tracks whether the form was submitted successfully or with an error
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const isFormSubmitted = formStatus === 'success' || formStatus === 'error';

  // Populate the Form.io #order-items container with submitted items table
  useEffect(() => {
    if (formStatus !== 'success') return;

    const injectTable = () => {
      const container = panelRef.current?.querySelector<HTMLElement>('#order-items');
      if (!container) return;

      const rows = itemsRef.current
        .map((cartItem) => {
          return `<tr><td>${cartItem.title ?? ''}</td><td><p>${cartItem.quantity}</p></td></tr>`;
        })
        .join('');

      const itemHeader = props?.item_label ?? 'Item';
      const quantityHeader = props?.quantity_label ?? 'Quantity';
      container.innerHTML = `<table class="table table-bordered" style="width:100%;table-layout:fixed"><thead><tr><th style="width:50%">${itemHeader}</th><th style="width:50%">${quantityHeader}</th></tr></thead><tbody>${rows}</tbody></table>`;
    };

    requestAnimationFrame(injectTable);
  }, [formStatus]);

  // Hold raw typed values per row only while they are over the limit, so the invalid entry stays visible
  const [overLimitDrafts, setOverLimitDrafts] = useState<Record<string, string>>({});

  const styles = orderSummaryVariants();

  // Lock body scroll while open and restore focus to the trigger on close. The trigger is
  // captured by CartProvider.openCart (synchronously, before it unmounts) rather than here,
  // since by the time this effect runs the trigger has already been removed from the DOM.
  // Re-runs on every isOpen flip (not just mount) since the panel stays mounted between opens.
  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    const elementToRefocus = previousFocusRef.current;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();

    return () => {
      document.body.style.overflow = originalOverflow;
      elementToRefocus?.focus();
    };
  }, [isOpen, previousFocusRef]);

  // Close the panel when Escape is pressed, only while it is actually open
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCart();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeCart]);

  // Close only when the dark backdrop (not the white panel) is clicked
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) closeCart();
    },
    [closeCart]
  );

  // Clear any held over-limit draft for a row once its value is valid again
  const clearDraft = useCallback((id: string) => {
    setOverLimitDrafts((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  // Allow only digits; empty input defaults to 1. Over-limit values are kept as drafts and blocked.
  const handleQuantityChange = useCallback(
    (id: string, raw: string) => {
      if (raw === '') {
        clearDraft(id);
        updateQuantity(id, 1);
        return;
      }
      if (!/^\d+$/.test(raw)) return;
      const value = parseInt(raw, 10);
      if (value < 1) return;
      if (value > maxAllowedQuantity) {
        setOverLimitDrafts((prev) => ({ ...prev, [id]: raw }));
        return;
      }
      clearDraft(id);
      updateQuantity(id, value);
    },
    [updateQuantity, maxAllowedQuantity, clearDraft]
  );

  return createPortal(
    <div
      className={styles.overlay({ hidden: !isOpen })}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-hidden={!isOpen}
      aria-label={props?.summary_title}
    >
      <div ref={panelRef} className={styles.panel()} tabIndex={-1}>
        <div className={styles.closeButtonWrapper()}>
          <button
            type="button"
            className={styles.closeButton()}
            onClick={closeCart}
            aria-label="close-order-summary"
          >
            <i className={styles.closeIcon()} />
          </button>
        </div>
        <div className={styles.content()}>
          <h2 className={styles.title()}>{props?.summary_title}</h2>
            {props?.summary_short_description && (
              <p className={styles.subtitle()}>
                {injectNumberOfItemsWithinLabel(props.summary_short_description, totalItems)}
              </p>
            )}

            <div className={styles.infoBox()}>
              <RichTextWrapper content={props?.summary_alert} />
            </div>

            {!isFormSubmitted && (
              <table className={styles.table()}>
                <thead>
                  <tr className={styles.headRow()}>
                    <th scope="col" className={styles.th()}>
                      {props?.quantity_label}
                    </th>
                    <th scope="col" className={styles.th()}>
                      {props?.item_label}
                    </th>
                    <th scope="col" className={styles.th({ class: styles.thRemove() })}>
                      {props?.remove_label}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const draft = overLimitDrafts[item.id];
                    const isOverLimit = draft !== undefined;
                    return (
                      <tr key={item.id} className={styles.bodyRow()}>
                        <td className={styles.tdQuantity()}>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={draft ?? item.quantity}
                            onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                            className={styles.quantityInput({ error: isOverLimit })}
                            aria-label={`${props?.quantity_label} for ${item.title}`}
                            aria-invalid={isOverLimit}
                            max={maxAllowedQuantity}
                          />
                        </td>
                        <td className={styles.tdItem()}>
                          <RichTextWrapper content={item.title} />
                          {isOverLimit && (
                            <p role="alert" className={styles.quantityError()}>
                              {maxQuantityErrorMessage}
                            </p>
                          )}
                        </td>
                        <td className={styles.tdRemove()}>
                          <button
                            type="button"
                            className={styles.removeButton()}
                            onClick={(e) => {
                              trackAdobeClick(
                                {
                                  clickName: `Remove - ${item.title?.replace(/<[^>]*>/g, '') ?? ''}`,
                                  clickLocation: 'Learning-Materials-Cart',
                                  clickType: 'cart-remove-button',
                                },
                                e
                              );
                              removeItem(item.id);
                            }}
                            aria-label={`Delete ${item.title}`}
                          >
                            <i className={styles.removeButtonIcon()} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          <button
            type="button"
            className={styles.browseLink()}
            onClick={(e) => {
              trackAdobeClick(
                {
                  clickName: props?.browse_more_label || 'Browse more',
                  clickLocation: 'Learning-Materials-Cart',
                  clickType: 'cart-link',
                },
                e
              );
              closeCart();
            }}
          >
            {props?.browse_more_label}
          </button>

          {!(!props?.forms?.form || items?.length <= 0) ? (
            <div className={styles.formWrapper()}>
              <Forms
                {...props.forms}
                form={props.forms.form as { form: import('@formio/react').FormType }}
                componentName="Forms"
                getSubmissionData={getSubmissionData}
                onSubmitStatus={setFormStatus}
              />
            </div>
          ) : <span className="text-input-error! py-1.5 px-3">Please select a material to order.</span>}
        </div>
      </div>
    </div>,
    document.body
  );
};
